import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiUser, FiCheck, FiAlertTriangle,
  FiRefreshCw, FiArrowLeft, FiX, FiArrowRight,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import { useToast } from '../../context/ToastContext';


const getToken   = () => localStorage.getItem('toolboxToken') || '';
const getNcToken = () => localStorage.getItem('amsToken')     || '';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const STATE_FULL_TO_ABBR: Record<string, string> = {
  'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
  'colorado':'CO','connecticut':'CT','delaware':'DE','florida':'FL','georgia':'GA',
  'hawaii':'HI','idaho':'ID','illinois':'IL','indiana':'IN','iowa':'IA',
  'kansas':'KS','kentucky':'KY','louisiana':'LA','maine':'ME','maryland':'MD',
  'massachusetts':'MA','michigan':'MI','minnesota':'MN','mississippi':'MS',
  'missouri':'MO','montana':'MT','nebraska':'NE','nevada':'NV',
  'new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY',
  'north carolina':'NC','north dakota':'ND','ohio':'OH','oklahoma':'OK',
  'oregon':'OR','pennsylvania':'PA','rhode island':'RI','south carolina':'SC',
  'south dakota':'SD','tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT',
  'virginia':'VA','washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY',
};

const toStateAbbr = (val: string): string => {
  if (!val) return '';
  const t = val.trim();
  if (US_STATES.includes(t.toUpperCase())) return t.toUpperCase();
  return STATE_FULL_TO_ABBR[t.toLowerCase()] || '';
};

interface Client {
  databaseId: string;
  name: string;
  commercialName?: string;
  firstName?: string;
  lastName?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  insuredType?: number;
}

interface Driver {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  age: number;
  gender: string;
}

interface Vehicle {
  vin: string;
  year: number;
  make: string;
  model: string;
  bodyStyle: string;
  registeredState: string;
  expirationDate: string;
}

interface Props {
  onGoToHistory: () => void;
}

const FenrisDriverVehicleSearch: React.FC<Props> = ({ onGoToHistory }) => {
  const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing');

  /* Existing client search */
  const [clientSearch, setClientSearch]   = useState('');
  const [clientName, setClientName]       = useState('');
  const [clientDbId, setClientDbId]       = useState('');
  const [clientList, setClientList]       = useState<Client[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError]     = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* New client name */
  const [newClientName, setNewClientName] = useState('');

  /* Person fields */
  const [firstName, setFirstName]       = useState('');
  const [lastName, setLastName]         = useState('');
  const [dateOfBirth, setDateOfBirth]   = useState('');

  /* Address form */
  const [address, setAddress] = useState('');
  const [city, setCity]       = useState('');
  const [state, setState]     = useState('');
  const [zip, setZip]         = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  /* Results */
  const [searching, setSearching]         = useState(false);
  const [drivers, setDrivers]             = useState<Driver[]>([]);
  const [vehicles, setVehicles]           = useState<Vehicle[]>([]);
  const [transactionId, setTransactionId] = useState('');
  const [insuredId, setInsuredId]         = useState('');
  const [activeTab, setActiveTab]         = useState<'Driver' | 'Vehicle'>('Driver');
  const [showResults, setShowResults]     = useState(false);
  const [noData, setNoData]               = useState(false);

  /* AMS submit state per tab */
  const [driverAddedInAMS, setDriverAddedInAMS]   = useState(false);
  const [vehicleAddedInAMS, setVehicleAddedInAMS] = useState(false);
  const [addingLoading, setAddingLoading]         = useState(false);

  const { showSuccess, showError: showErr } = useToast();

  /* Client search — debounced */
  const fetchClients = async (query: string) => {
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlist?searchText=${encodeURIComponent(query)}&nowCertsToken=${getNcToken()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setClientList(res.data || []);
    } catch { setClientList([]); }
    finally { setClientLoading(false); }
  };

  useEffect(() => {
    if (!clientSearch.trim() || clientDbId) { setClientList([]); setClientLoading(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setClientLoading(true);
    debounceRef.current = setTimeout(() => fetchClients(clientSearch), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [clientSearch, clientDbId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClientSelect = useCallback((c: Client) => {
    const displayName = c.insuredType === 0
      ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name
      : c.commercialName || c.name;
    setClientName(displayName);
    setClientDbId(c.databaseId);
    setClientSearch(displayName);
    setClientList([]);
    setClientError('');
    setAddress(c.addressLine1 || '');
    setCity(c.city || '');
    setState(toStateAbbr(c.state || ''));
    setZip(c.zipCode || '');
    /* prefill first/last from client record */
    setFirstName(c.firstName || '');
    setLastName(c.lastName || '');
    /* fetch full details to get all fields */
    fetchClientById(c.databaseId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClientById = async (id: string) => {
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlistbyId?insuredDatabaseId=${id}&nowCertsToken=${getNcToken()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const info = res.data?.[0];
      if (info) {
        setAddress(info.addressLine1 || '');
        setCity(info.city || '');
        setState(toStateAbbr(info.stateNameOrAbbreviation || ''));
        setZip(info.zipCode || '');
        setFirstName(info.firstName || '');
        setLastName(info.lastName || '');
      }
    } catch {}
  };

  /* Validation */
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (clientMode === 'existing' && !clientDbId) errs.client = 'Please select a client';
    if (clientMode === 'new' && !newClientName.trim()) errs.newClientName = 'Client name is required';
    if (!address.trim())    errs.address   = 'Address is required';
    if (!city.trim())       errs.city      = 'City is required';
    if (!state)             errs.state     = 'State is required';
    if (!/^\d{5}(-\d{4})?$/.test(zip)) errs.zip = 'Valid zip code required';
    setFormErrors(errs);
    if (errs.client) setClientError(errs.client);
    return Object.keys(errs).length === 0;
  };

  const onSearch = useCallback(async () => {
    if (!validate()) return;
    setSearching(true);
    setShowResults(false);
    setNoData(false);
    setDriverAddedInAMS(false);
    setVehicleAddedInAMS(false);

    try {
      let resolvedInsuredId = clientDbId;

      if (clientMode === 'new') {
        const parts = newClientName.trim().split(' ');
        const createRes = await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/insertinsured`,
          {
            commercialName: newClientName.trim(),
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
            dba: '', addressLine1: address, addressLine2: '',
            state, city, zipCode: zip, eMail: '', cellPhone: '', fein: '', insuredType: 1,
          },
          {
            params: { nowCertsToken: getNcToken() },
            headers: { Authorization: `Bearer ${getToken()}` },
          },
        );
        resolvedInsuredId = createRes.data?.database_id || '';
      }

      setInsuredId(resolvedInsuredId);

      const selectionOptions = JSON.stringify({
        existingInsuredSearch: clientMode === 'existing',
        clientName: clientMode === 'new' ? newClientName : clientName,
        newInsuredCreate: clientMode === 'new',
        insuredDatabaseId: resolvedInsuredId,
      });

      const searchRes = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/FenrisDriverAndVehicle/search`,
        {
          selectionOptionsJson: selectionOptions,
          Address: { Address1: address, City: city, State: state, Zip: zip },
          Person:  { firstName, lastName, dateOfBirth },
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      const data = searchRes.data;
      if (data?.success === true && data?.responseJson) {
        setTransactionId(data.responseJson.submissionId || '');
        setDrivers(data.responseJson.drivers ?? []);
        setVehicles(data.responseJson.vehiclesEnhanced ?? []);
        setShowResults(true);
        setActiveTab('Driver');
        showSuccess('Driver and vehicle data retrieved successfully');
      } else {
        showErr(data?.errorMessage || 'No data found for this person/address');
        setNoData(true);
      }
    } catch (err: any) {
      showErr(err?.response?.data?.errorMessage || 'Failed to retrieve Fenris data');
      setNoData(true);
    } finally {
      setSearching(false);
    }
  }, [clientMode, clientDbId, newClientName, clientName, firstName, lastName, dateOfBirth, address, city, state, zip]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateHistory = useCallback((driverAdded: boolean, vehicleAdded: boolean) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/FenrisDriverAndVehicle/update`,
      {
        transactionId,
        submittedJsonData: JSON.stringify({ driverDetails: drivers, vehicleDetails: vehicles }),
        isDriverAddedInAMS: driverAdded,
        isVehicleAddedInAMS: vehicleAdded,
      },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).catch(() => {});
  }, [transactionId, drivers, vehicles]);

  const addDriversToAMS = useCallback(async () => {
    setAddingLoading(true);
    try {
      const payload = drivers.map(d => ({
        insured_database_id: insuredId,
        first_name: d.firstName,
        last_name: d.lastName,
        license_number: '',
        date_of_birth: d.dateOfBirth,
        active: true,
      }));
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/adddrivers?ncToken=${getNcToken()}`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const results: any[] = Array.isArray(res.data) ? res.data : [];
      const successes = results.filter(r => String(r.status) === '1');
      const failures  = results.filter(r => String(r.status) !== '1');
      if (successes.length > 0) {
        setDriverAddedInAMS(true);
        updateHistory(true, vehicleAddedInAMS);
        showSuccess('Drivers successfully added in AMS');
      }
      if (failures.length > 0) {
        showErr(failures.map(r => r.message).join(' | '));
      }
    } catch {
      showErr('Failed to add drivers to AMS');
    } finally {
      setAddingLoading(false);
    }
  }, [drivers, insuredId, vehicleAddedInAMS, updateHistory]);

  const addVehiclesToAMS = useCallback(async () => {
    setAddingLoading(true);
    try {
      const payload = vehicles.map(v => ({
        insured_database_id: insuredId,
        vinNumber: v.vin,
        vehicleManufacturer: v.make,
        vehicleType: v.bodyStyle,
        year: v.year,
        insured_first_name: firstName,
        insured_last_name: lastName,
        model: v.model,
        registration_expiration_date: v.expirationDate,
        vehicleEditAdditional3: { registration_state: v.registeredState },
        check: true,
      }));
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addvehicles?ncToken=${getNcToken()}`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const results: any[] = Array.isArray(res.data) ? res.data : [];
      const successes = results.filter(r => String(r.status) === '1');
      const failures  = results.filter(r => String(r.status) !== '1');
      if (successes.length > 0) {
        setVehicleAddedInAMS(true);
        updateHistory(driverAddedInAMS, true);
        showSuccess('Vehicles successfully added in AMS');
      }
      if (failures.length > 0) {
        showErr(failures.map(r => r.message).join(' | '));
      }
    } catch {
      showErr('Failed to add vehicles to AMS');
    } finally {
      setAddingLoading(false);
    }
  }, [vehicles, insuredId, firstName, lastName, driverAddedInAMS, updateHistory]);

  const onReset = () => {
    setClientMode('existing');
    setClientSearch(''); setClientName(''); setClientDbId('');
    setClientList([]); setClientError('');
    setNewClientName('');
    setFirstName(''); setLastName(''); setDateOfBirth('');
    setAddress(''); setCity(''); setState(''); setZip('');
    setFormErrors({});
    setDrivers([]); setVehicles([]);
    setShowResults(false); setNoData(false);
    setTransactionId(''); setInsuredId('');
    setDriverAddedInAMS(false); setVehicleAddedInAMS(false);
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div className="ho-search" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>

        {/* ── Search Form ── */}
        {!showResults && !noData && (
          <div className="ho-card ho-card--form">

            {/* Step 1 — Client */}
            <div className="ho-step-label">
              <span className="ho-step-num">1</span> Client
            </div>
            <div className="ho-toggle-group">
              <button
                className={`ho-toggle${clientMode === 'existing' ? ' ho-toggle--active' : ''}`}
                onClick={() => { setClientMode('existing'); setClientSearch(''); setClientName(''); setClientDbId(''); setClientList([]); }}
              >
                <FiSearch size={14} /> Use Existing Client
              </button>
              <button
                className={`ho-toggle${clientMode === 'new' ? ' ho-toggle--active' : ''}`}
                onClick={() => { setClientMode('new'); setClientSearch(''); setClientName(''); setClientDbId(''); setClientList([]); }}
              >
                <FiUser size={14} /> Create New Client
              </button>
            </div>

            {clientMode === 'existing' && (
              <div className="cs-client-search">
                <div className="cs-client-search__input-wrap" style={{ borderColor: clientError ? 'var(--accent-red, #ef4444)' : undefined }}>
                  <FiSearch size={15} className="cs-client-search__icon" />
                  <input
                    className="cs-client-search__input"
                    type="text"
                    placeholder="Search by client name…"
                    value={clientSearch}
                    onChange={e => {
                      setClientSearch(e.target.value);
                      if (clientError) setClientError('');
                      if (clientName) { setClientName(''); setClientDbId(''); }
                    }}
                  />
                  {clientLoading && <span className="cs-client-search__spinner" />}
                  {clientName && !clientLoading && (
                    <button className="cs-client-search__clear" onClick={() => { setClientSearch(''); setClientName(''); setClientDbId(''); setClientList([]); setClientError(''); setFirstName(''); setLastName(''); setAddress(''); setCity(''); setState(''); setZip(''); }}>
                      <FiX size={14} />
                    </button>
                  )}
                </div>
                {clientList.length > 0 && (
                  <ul className="cs-client-dropdown">
                    {clientList.map(c => {
                      const name = c.insuredType === 0 ? `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name : c.commercialName || c.name;
                      return (
                        <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => onClientSelect(c)}>
                          <span className="cs-client-dropdown__name">{name}</span>
                          {c.addressLine1 && <span className="cs-client-dropdown__email">{c.addressLine1}, {c.city}</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {clientSearch.trim() && !clientLoading && clientList.length === 0 && !clientName && (
                  <ul className="cs-client-dropdown">
                    <li className="cs-client-dropdown__item" style={{ cursor: 'default', color: 'var(--text-secondary)', opacity: 0.8 }}>No clients found</li>
                  </ul>
                )}
                {clientError && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>{clientError}</span>}
              </div>
            )}

            {clientMode === 'new' && (
              <div className="cs-client-search">
                <div className="cs-client-search__input-wrap" style={{ borderColor: formErrors.newClientName ? 'var(--accent-red, #ef4444)' : undefined }}>
                  <FiUser size={15} className="cs-client-search__icon" />
                  <input className="cs-client-search__input" type="text" placeholder="Client / Business Name" value={newClientName} maxLength={150} onChange={e => setNewClientName(e.target.value)} />
                </div>
                {formErrors.newClientName && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 4, display: 'block' }}>{formErrors.newClientName}</span>}
              </div>
            )}

            {/* Step 2 — Address */}
            {(clientMode === 'new' || clientDbId) && (
              <>
                <div className="ho-step-divider" />
                <div className="ho-step-label">
                  <span className="ho-step-num">2</span> Address
                </div>
                <div className="ho-form-grid">
                  <div className="ho-form-field ho-form-field--full">
                    <label className="ho-form-label">Address Line 1</label>
                    <div className="cs-client-search__input-wrap" style={{ borderColor: formErrors.address ? 'var(--accent-red, #ef4444)' : undefined }}>
                      <input className="cs-client-search__input" type="text" placeholder="123 Main Street" value={address} maxLength={255} onChange={e => setAddress(e.target.value)} />
                    </div>
                    {formErrors.address && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.address}</span>}
                  </div>
                  <div className="ho-form-field">
                    <label className="ho-form-label">City</label>
                    <div className="cs-client-search__input-wrap" style={{ borderColor: formErrors.city ? 'var(--accent-red, #ef4444)' : undefined }}>
                      <input className="cs-client-search__input" type="text" placeholder="City" value={city} maxLength={50} onChange={e => setCity(e.target.value)} />
                    </div>
                    {formErrors.city && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.city}</span>}
                  </div>
                  <div className="ho-form-field">
                    <label className="ho-form-label">State</label>
                    <div className="ho-select-wrap">
                      <select className={`ho-select${formErrors.state ? ' ho-input--error' : ''}`} value={state} onChange={e => setState(e.target.value)}>
                        <option value="">Select state</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    {formErrors.state && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.state}</span>}
                  </div>
                  <div className="ho-form-field">
                    <label className="ho-form-label">Zip Code</label>
                    <div className="cs-client-search__input-wrap" style={{ borderColor: formErrors.zip ? 'var(--accent-red, #ef4444)' : undefined }}>
                      <input className="cs-client-search__input" type="text" placeholder="12345" value={zip} maxLength={10} onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 9))} />
                    </div>
                    {formErrors.zip && <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>{formErrors.zip}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <button className="cs-browse-btn" onClick={onSearch} disabled={searching}>
                    {searching ? <><FiRefreshCw size={13} className="ho-spin" style={{ marginRight: 6 }} />Searching Fenris…</> : 'Get Driver & Vehicle Data'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── No Data ── */}
        {noData && (
          <motion.div className="ho-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button className="ho-back-btn" onClick={onReset}><FiArrowLeft size={14} /> Back</button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px', borderRadius: 10, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.25)' }}>
              <FiAlertTriangle size={18} style={{ color: '#fbbf24', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>No data found</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>Fenris returned no records for this person/address.</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Results ── */}
        {showResults && (
          <motion.div className="ho-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <div className="ho-result-topbar">
              <button className="ho-back-btn" onClick={onReset}><FiArrowLeft size={14} /> Back</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border-glass)' }}>
              {(['Driver', 'Vehicle'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    background: 'none', border: 'none',
                    borderBottom: activeTab === tab ? '2px solid var(--accent-purple, #a200ff)' : '2px solid transparent',
                    color: activeTab === tab ? 'var(--accent-purple, #a200ff)' : 'var(--text-secondary)',
                  }}
                >
                  {tab} Details {tab === 'Driver' ? `(${drivers.length})` : `(${vehicles.length})`}
                </button>
              ))}
            </div>

            {/* Driver Tab */}
            {activeTab === 'Driver' && (
              <>
                {drivers.length === 0 ? (
                  <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No drivers found</div>
                ) : (
                  <div className="qp-table-wrap glass-surface">
                    <table className="qp-table">
                      <thead>
                        <tr>
                          <th className="qp-table__th">First Name</th>
                          <th className="qp-table__th">Last Name</th>
                          <th className="qp-table__th">Date of Birth</th>
                          <th className="qp-table__th">Age</th>
                          <th className="qp-table__th">Gender</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drivers.map((d, i) => (
                          <tr key={i} className="qp-table__row">
                            <td className="qp-table__td">{d.firstName}</td>
                            <td className="qp-table__td">{d.lastName}</td>
                            <td className="qp-table__td">{formatDate(d.dateOfBirth)}</td>
                            <td className="qp-table__td">{d.age}</td>
                            <td className="qp-table__td">{d.gender}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!driverAddedInAMS && drivers.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button className="cs-browse-btn cs-browse-btn--sm" onClick={addDriversToAMS} disabled={addingLoading}>
                      {addingLoading ? <><FiRefreshCw size={13} className="ho-spin" style={{ marginRight: 6 }} />Adding…</> : 'Add Drivers to AMS'}
                    </button>
                  </div>
                )}
                {driverAddedInAMS && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <span className="ho-badge ho-badge--success"><FiCheck size={11} /> Drivers Added to AMS</span>
                  </div>
                )}
              </>
            )}

            {/* Vehicle Tab */}
            {activeTab === 'Vehicle' && (
              <>
                {vehicles.length === 0 ? (
                  <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No vehicles found</div>
                ) : (
                  <div className="qp-table-wrap glass-surface">
                    <table className="qp-table">
                      <thead>
                        <tr>
                          <th className="qp-table__th">VIN</th>
                          <th className="qp-table__th">Year</th>
                          <th className="qp-table__th">Make</th>
                          <th className="qp-table__th">Model</th>
                          <th className="qp-table__th">Body Style</th>
                          <th className="qp-table__th">Reg. State</th>
                          <th className="qp-table__th">Expiration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicles.map((v, i) => (
                          <tr key={i} className="qp-table__row">
                            <td className="qp-table__td" style={{ fontFamily: 'monospace', fontSize: 11 }}>{v.vin}</td>
                            <td className="qp-table__td">{v.year}</td>
                            <td className="qp-table__td">{v.make}</td>
                            <td className="qp-table__td">{v.model}</td>
                            <td className="qp-table__td">{v.bodyStyle}</td>
                            <td className="qp-table__td">{v.registeredState}</td>
                            <td className="qp-table__td">{formatDate(v.expirationDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!vehicleAddedInAMS && vehicles.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button className="cs-browse-btn cs-browse-btn--sm" onClick={addVehiclesToAMS} disabled={addingLoading}>
                      {addingLoading ? <><FiRefreshCw size={13} className="ho-spin" style={{ marginRight: 6 }} />Adding…</> : 'Add Vehicles to AMS'}
                    </button>
                  </div>
                )}
                {vehicleAddedInAMS && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <span className="ho-badge ho-badge--success"><FiCheck size={11} /> Vehicles Added to AMS</span>
                  </div>
                )}
              </>
            )}

            {(driverAddedInAMS || vehicleAddedInAMS) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
                <button className="ho-link-btn" onClick={onGoToHistory}>View in History <FiArrowRight size={13} /></button>
                <button className="ho-link-btn" onClick={onReset}>New Search</button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </>
  );
};

export default FenrisDriverVehicleSearch;

